import { useSyncExternalStore } from "react";
import { forcedReducedMotion } from "../lib/interaction-preferences";
import { useMediaQuery } from "./use-media-query";

// One MutationObserver on `<html>` for every caller. A map draws a glyph per
// marker and each glyph reads this hook, so a per-caller observer meant
// hundreds of observers on the same attribute. The observer is created on the
// first subscribe and dropped with the last, never at module scope (a
// consumer test runs without a DOM).
const forcedListeners = new Set<() => void>();
let forcedObserver: MutationObserver | null = null;

function subscribeForced(callback: () => void): () => void {
  forcedListeners.add(callback);
  if (!forcedObserver) {
    forcedObserver = new MutationObserver(() => {
      for (const listener of forcedListeners) listener();
    });
    forcedObserver.observe(document.documentElement, {
      attributeFilter: ["data-reduce-motion"],
    });
  }
  return () => {
    forcedListeners.delete(callback);
    if (forcedListeners.size === 0) {
      forcedObserver?.disconnect();
      forcedObserver = null;
    }
  };
}

/**
 * Whether motion should be minimized. JS-side signal for animation the CSS
 * engine doesn't drive (WAAPI springs, animated chart series) — CSS-driven
 * motion uses the `motion-reduce:` variant instead.
 *
 * Two sources, same precedence as the stylesheet: a forced preference on
 * `<html>` (`applyMotionPreference`) wins; with none, the OS
 * `prefers-reduced-motion` query answers.
 */
export function usePrefersReducedMotion(): boolean {
  const system = useMediaQuery("(prefers-reduced-motion: reduce)");

  const forced = useSyncExternalStore(
    subscribeForced,
    forcedReducedMotion,
    getServerSnapshot,
  );

  return forced ?? system;
}

// The server has no document; `null` defers to the media query's own server
// snapshot (false), and hydration corrects both together.
function getServerSnapshot(): boolean | null {
  return null;
}
